import addressApi, { MUTATION_SYNC_UNITS } from "api/landlord/address";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { InputSize, Show } from "shared/components";
import { BaseInput } from "shared/components/forms/BaseInput";
import { getErrorMessage } from "utils/error";
import { AddUnitModal } from "./components/AddUnitModal";
import { useAuthContext } from "providers/Auth";
import { UnitsTable } from "./components/UnitsTable";
// import { InviteApplicantsModal } from "shared/app/InviteApplicantsModal";
import { DeleteUnitModal } from "./components/DeleteUnitModal";
import { LayoutButtons } from "./components/LayoutButtons";
import { Pagination } from "shared/components/Pagination";
import {
  useUnitsTableController,
  type FraudAlertsFilter,
  type UnitsViewMode
} from "./controller";
import { DEFAULT_UNITS_QUERY, unitDetailsPath } from "./constants";
import { PropertyManagementSystemSync } from "shared/components/PropertyManagementSystemSync";
import { toast } from "react-toastify";
import { Tabs, type Tab } from "shared/components/BaseTabs";
import { cx } from "shared/utils";
import { useMedia } from "shared/providers";
import { Unit } from "types/landlord/address";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { InviteAndCreateModal } from "../InviteAndCreateModal";
import { SortingState } from "@tanstack/react-table";
import { Property } from "shared/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { UnitsCardGrid } from "./components/UnitsCardGrid";
import { UnitsFiltersDrawer } from "./components/UnitsFiltersDrawer";
import { NeedsLockboxBanner } from "./components/NeedsLockboxBanner";
import { useNeedsLockboxCount } from "shared/hooks/use-needs-lockbox-count";
import {
  invalidateNeedsLockboxCounts,
  invalidateUnitsLists
} from "shared/utils/units-cache";
import {
  countActiveUnitsFilters,
  EMPTY_UNITS_FILTERS,
  NO_LOCKBOX_FILTER_PARAM,
  NO_LOCKBOX_FILTER_VALUE,
  UnitsFilters
} from "./components/UnitsFiltersDrawer/types";
import {
  FilterIcon,
  FilterVerticalIcon,
  RealEstate01Icon,
  Shield01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { VisibilityPanel } from "shared/components/VisibilityPanel";
import { useColumnVisibility } from "shared/hooks/use-column-visibility";
import {
  DEFAULT_UNITS_CARD_FIELDS,
  DEFAULT_UNITS_CARD_FIELDS_WITHOUT_FRAUD_ALERTS,
  DEFAULT_UNITS_TABLE_COLUMNS,
  DEFAULT_UNITS_TABLE_COLUMNS_WITHOUT_FRAUD_ALERTS,
  UNITS_CARD_FIELDS_TAB_KEY,
  UNITS_TABLE_COLUMNS_TAB_KEY
} from "./visibilityConstants";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components";

enum UnitTabs {
  ALL = "All",
  ALLOWED = "Applications allowed",
  DISALLOWED = "Applications disallowed"
}

type ListedFilter = "all" | "listed" | "unlisted";

const LISTED_TABS: Tab<ListedFilter>[] = [
  { value: "all", label: "All" },
  { value: "listed", label: "Listed" },
  { value: "unlisted", label: "Unlisted" }
];

const LISTED_VALUE: Record<ListedFilter, boolean | null> = {
  all: null,
  listed: true,
  unlisted: false
};

type FraudAlertsTab = "all" | FraudAlertsFilter;

const FRAUD_ALERTS_TABS: Tab<FraudAlertsTab>[] = [
  { value: "all", label: "All" },
  { value: "with", label: "Fraudulent listings" },
  { value: "without", label: "No fraudulent listings" }
];

const FRAUD_ALERTS_EMPTY_STATE: Record<
  FraudAlertsFilter,
  { title: string; text: string }
> = {
  with: {
    title: "No fraudulent listings",
    text: "None of your units have an open fraud alert right now."
  },
  without: {
    title: "No units without alerts",
    text: "Every unit in this view has an open fraud alert."
  }
};

const LISTINGS_EMPTY_STATE = {
  Icon: RealEstate01Icon,
  title: "No listings",
  text: "Units you list show up here. Turn on List unit for a unit on the Units page."
};

const NO_MATCHING_DATA_TEXT = "We couldn't find any data matching your query";

const getVariantView = ({
  isListings,
  isBoomScreen,
  Units,
  units,
  hasSearch,
  hasQuery
}: {
  isListings: boolean;
  isBoomScreen: boolean;
  Units: string;
  units: string;
  hasSearch: boolean;
  hasQuery: boolean;
}) =>
  isListings
    ? {
        title: "Listings",
        searchPlaceholder: "Search listings",
        showScreeningTabs: false,
        hasSearchParams: hasQuery,
        emptyState: hasQuery ? undefined : LISTINGS_EMPTY_STATE,
        cardEmptyText: hasQuery
          ? NO_MATCHING_DATA_TEXT
          : LISTINGS_EMPTY_STATE.text
      }
    : {
        title: Units,
        searchPlaceholder: `Search ${units}`,
        showScreeningTabs: isBoomScreen,
        hasSearchParams: hasSearch,
        emptyState: undefined,
        cardEmptyText: hasQuery ? NO_MATCHING_DATA_TEXT : `No ${units} to show`
      };

const fraudAlertsParam = (
  enabled: boolean,
  tab: FraudAlertsTab
): FraudAlertsFilter | null => (enabled && tab !== "all" ? tab : null);

// Module-level constants, so the visibility hook's `defaults` stay stable.
const unitsVisibilityDefaults = (fraudAlertsEnabled: boolean) =>
  fraudAlertsEnabled
    ? { table: DEFAULT_UNITS_TABLE_COLUMNS, card: DEFAULT_UNITS_CARD_FIELDS }
    : {
        table: DEFAULT_UNITS_TABLE_COLUMNS_WITHOUT_FRAUD_ALERTS,
        card: DEFAULT_UNITS_CARD_FIELDS_WITHOUT_FRAUD_ALERTS
      };

const initialUnitsFilters = (search: string): UnitsFilters => ({
  ...EMPTY_UNITS_FILTERS,
  no_lockbox:
    new URLSearchParams(search).get(NO_LOCKBOX_FILTER_PARAM) ===
    NO_LOCKBOX_FILTER_VALUE
});

const VIEW_MODE_TABS: Tab<UnitsViewMode>[] = [
  { value: "table", label: "List view" },
  { value: "card", label: "Card view" }
];

const UNIT_TABS: Tab<UnitTabs>[] = [
  { value: UnitTabs.ALL, label: UnitTabs.ALL },
  { value: UnitTabs.ALLOWED, label: UnitTabs.ALLOWED },
  { value: UnitTabs.DISALLOWED, label: UnitTabs.DISALLOWED }
];

export const UnitsTableView = ({
  property,
  query = DEFAULT_UNITS_QUERY,
  buttons,
  propertyId,
  showAddUnitModal: _showAddUnitModal,
  hideSync = false,
  viewPortHeightOffset,
  variant = "units"
}: {
  property?: Property;
  query?: string;
  buttons?: React.ReactNode;
  propertyId?: number;
  showAddUnitModal?: boolean;
  hideSync?: boolean;
  viewPortHeightOffset?: number;
  variant?: "units" | "listings";
}) => {
  const isListings = variant === "listings";
  const { units, Unit, Units } = useUnitsPropertiesDictionary();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showFilters, setShowFilters] = useState(false);
  const location = useLocation();
  const [filters, setFilters] = useState<UnitsFilters>(() =>
    initialUnitsFilters(location.search)
  );
  const activeFilterCount = countActiveUnitsFilters(filters);
  const [listedTab, setListedTab] = useState<ListedFilter>("all");
  const [fraudAlertsTab, setFraudAlertsTab] = useState<FraudAlertsTab>("all");
  const { type } = useAuthContext();
  const visibilityDefaults = unitsVisibilityDefaults(type.isListingFraudAlerts);
  const tableColumns = useColumnVisibility(
    UNITS_TABLE_COLUMNS_TAB_KEY,
    visibilityDefaults.table
  );
  const cardFields = useColumnVisibility(
    UNITS_CARD_FIELDS_TAB_KEY,
    visibilityDefaults.card
  );
  const hiddenTableColumnIds = useMemo(
    () =>
      new Set(tableColumns.columns.filter((c) => !c.checked).map((c) => c.id)),
    [tableColumns.columns]
  );

  const visibleCardFields = useMemo(
    () => new Set(cardFields.columns.filter((f) => f.checked).map((f) => f.id)),
    [cardFields.columns]
  );
  const { isMobile } = useMedia();
  const canAddUnit =
    type.adminPrivileges &&
    ((!type.isSDK && type.isBoomScreen) ||
      type.isManual ||
      type.canManageEntitiesDirectly);
  const [showDeleteUnitModal, setShowDeleteUnitModal] =
    useState<boolean>(false);
  const [showAddUnitModal, setShowAddUnitModal] = useState<boolean>(false);
  const [currentTab, setCurrentTab] = useState<UnitTabs>(UnitTabs.ALL);
  const [showInviteApplicantsModal, setShowInviteApplicantsModal] =
    useState<boolean>(false);
  const [selectedUnitId, setSelectedUnitId] = useState<number>();
  const [isSyncing, setIsSyncing] = useState(type.isPMS);
  const [unitsData, setUnitsData] = useState<Unit[] | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);

  const availableForApplication = (() => {
    if (currentTab === UnitTabs.ALLOWED) return true;
    if (currentTab === UnitTabs.DISALLOWED) return false;
    return null;
  })();

  const {
    isUnitsDashboard,
    isCardView,
    cardUnits,
    cardsQuery,
    data,
    refetch,
    debouncedParams,
    searchParams,
    setSearchParams,
    queryProperties,
    isFetching,
    isRefetching,
    syncData,
    syncError,
    searchTerm,
    setSearchTerm
  } = useUnitsTableController({
    query,
    isSyncing,
    available_for_application: availableForApplication,
    sorting,
    filters,
    listed: LISTED_VALUE[listedTab],
    fraudAlerts: fraudAlertsParam(type.isListingFraudAlerts, fraudAlertsTab),
    liveOnListings: isListings,
    defaultView: isListings ? "card" : "table"
  });

  // An empty fraud-alert filter is not "no units yet": explain it instead of offering "Add unit".
  const activeFraudAlertsFilter = fraudAlertsParam(
    type.isListingFraudAlerts,
    fraudAlertsTab
  );
  const fraudAlertsEmptyState =
    activeFraudAlertsFilter && !debouncedParams && activeFilterCount === 0
      ? {
          Icon: Shield01Icon,
          ...FRAUD_ALERTS_EMPTY_STATE[activeFraudAlertsFilter]
        }
      : undefined;
  // Refetched on every visit so a teammate's lockbox or template change shows up
  // the next time the Units page is opened.
  const needsLockboxCount = useNeedsLockboxCount({
    enabled: isUnitsDashboard,
    refetchOnMount: "always"
  });

  // Once the last flagged unit is fixed the banner (and its "Show all units"
  // button) disappears, so drop the filter with it instead of stranding the
  // user on an empty list.
  const previousNeedsLockboxCount = useRef(needsLockboxCount);
  useEffect(() => {
    const cleared =
      previousNeedsLockboxCount.current > 0 && needsLockboxCount === 0;
    previousNeedsLockboxCount.current = needsLockboxCount;
    if (cleared)
      setFilters((prev) =>
        prev.needs_lockbox ? { ...prev, needs_lockbox: false } : prev
      );
  }, [needsLockboxCount]);

  // The banner count ignores search, tabs and filters, so "View units" clears
  // them: the list then shows exactly the units the banner counts.
  const toggleNeedsLockboxFilter = () => {
    if (filters.needs_lockbox) {
      setFilters((prev) => ({ ...prev, needs_lockbox: false }));
      setSearchParams({ ...searchParams, page: "1" });
      return;
    }

    setFilters({ ...EMPTY_UNITS_FILTERS, needs_lockbox: true });
    setCurrentTab(UnitTabs.ALL);
    setListedTab("all");
    setFraudAlertsTab("all");
    setSearchParams({ ...searchParams, unit: "", page: "1" });
  };

  const variantView = getVariantView({
    isListings,
    isBoomScreen: type.isBoomScreen,
    Units,
    units,
    hasSearch: Boolean(debouncedParams),
    hasQuery: Boolean(debouncedParams) || activeFilterCount > 0
  });

  const listedCardUnits = useMemo(
    () => cardUnits.filter((unit) => unit.listed !== false),
    [cardUnits]
  );

  const {
    hasNextPage: cardsHasNextPage,
    isFetchingNextPage: cardsFetchingNext,
    fetchNextPage: fetchNextCards
  } = cardsQuery;

  const loadMoreCards = useCallback(() => {
    if (cardsHasNextPage && !cardsFetchingNext) fetchNextCards();
  }, [cardsHasNextPage, cardsFetchingNext, fetchNextCards]);

  const handleActivityClick = useCallback(
    (unit: Unit) => {
      navigate(
        unitDetailsPath(unit.property?.id, unit.id, {
          fromListings: isListings,
          tab: "activity-log"
        })
      );
    },
    [navigate, isListings]
  );

  const handleAvailableForApplication = (unit: Unit) => {
    setUnitsData((prev) => {
      const temp = prev || [];
      return temp.map((u) => (unit.id === u.id ? { ...u, ...unit } : u));
    });
    invalidateNeedsLockboxCounts(queryClient);
    if (isListings && unit.listed === false) invalidateUnitsLists(queryClient);
  };

  useEffect(() => {
    if (data) setUnitsData(data.items);
  }, [data]);

  const tableRows = useMemo(() => {
    const rows = unitsData || data?.items || [];
    return isListings ? rows.filter((unit) => unit.listed !== false) : rows;
  }, [unitsData, data, isListings]);

  useEffect(() => {
    if (typeof _showAddUnitModal === "boolean")
      setShowAddUnitModal(_showAddUnitModal);
  }, [_showAddUnitModal]);

  const deleteUnit = async () => {
    try {
      await addressApi.deleteUnit(selectedUnitId || 0);

      toast.success(`${Unit} was deleted successfully`);

      await refetch();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
    setShowDeleteUnitModal(false);
  };

  const handleCopyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.info("Link copied");
    } catch (err) {
      toast.error(`Error copying link: ${getErrorMessage(err)}`);
    }
  };

  const { mutate: handleRequestSync } = useMutation({
    mutationFn: async () => {
      return addressApi
        .requestPropertyManagementSystemSync("units")
        .then(() => {
          toast.success(`Sync ${units} requested successfully`);
          setIsSyncing(true);
        })
        .catch((error) => {
          toast.error(getErrorMessage(error));
          setIsSyncing(false);
        });
    },
    mutationKey: [MUTATION_SYNC_UNITS]
  });

  useEffect(() => {
    setIsSyncing(type.isPMS);
  }, [type.isPMS]);

  useEffect(() => {
    const isSyncRunning = syncData?.status === "running";
    setIsSyncing(isSyncRunning);

    if (!isSyncRunning) refetch();
  }, [syncData, syncError]);

  useEffect(() => {
    setSearchParams({
      ...searchParams,
      page: "1"
    });
    refetch();
  }, [currentTab]);

  const { propertyIdProp, propertyName, unitName } = useMemo(() => {
    const propertyIdProp = propertyId
      ? propertyId
      : data?.items.find((u) => u.id === selectedUnitId)?.property.id;

    const propertyName =
      data?.items.find((u) => u.id === selectedUnitId)?.property.name ||
      data?.items.find((u) => u.id === selectedUnitId)?.property.address
        .address1;

    const unitName = data?.items.find((u) => u.id === selectedUnitId)?.title;

    return { propertyIdProp, propertyName, unitName };
  }, [propertyId, selectedUnitId, data]);

  return (
    <div className="animate-appear pb-[73px] md:pb-0 w-full">
      <div className="pb-4 w-full">
        <Show if={isUnitsDashboard}>
          <p className="m-0 mb-6 text-2xl font-bold text-content-1000">
            {variantView.title}
          </p>
        </Show>
        <Show if={isUnitsDashboard && needsLockboxCount > 0}>
          <NeedsLockboxBanner
            count={needsLockboxCount}
            isFiltered={filters.needs_lockbox}
            onToggle={toggleNeedsLockboxFilter}
          />
        </Show>
        <div className="flex justify-between w-full flex-wrap md:flex-nowrap md:max-w-[calc(100vw-120px)]">
          <div className="flex w-full gap-2 items-center">
            <BaseInput
              id="search"
              placeholder={variantView.searchPlaceholder}
              value={searchParams.unit}
              onChange={(value) =>
                setSearchParams({
                  ...searchParams,
                  unit: value,
                  page: "1"
                })
              }
              className="!mb-0 w-full max-w-[280px] h-12 md:h-10"
              size={InputSize.SM}
            />

            {!hideSync && (
              <PropertyManagementSystemSync
                isSyncing={isSyncing}
                handleRequestSync={handleRequestSync}
                lastSuccessSyncedAt={syncData?.last_successful_sync}
              />
            )}
          </div>

          <div
            className={cx(
              isMobile && !buttons ? "hidden" : "flex",
              "gap-2 ml-auto md:w-auto w-full flex-nowrap pt-4 md:pt-0 items-center"
            )}
          >
            <Show if={isListings}>
              <div className="md:w-fit">
                <Tabs<UnitsViewMode>
                  tabStyle="outline"
                  size="sm"
                  onChange={(mode) =>
                    setSearchParams({ ...searchParams, view: mode, page: "1" })
                  }
                  active={isCardView ? "card" : "table"}
                  tabs={VIEW_MODE_TABS}
                  className="h-10"
                />
              </div>
            </Show>
            {buttons ? (
              buttons
            ) : (
              <LayoutButtons
                setShowAddUnitModal={
                  isListings ? undefined : setShowAddUnitModal
                }
                handleCopyLink={handleCopyLink}
              />
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Show if={isUnitsDashboard}>
            <IconButton
              variant={IconButtonVariant.BASE}
              size={IconButtonSize.SM}
              HugeIcon={FilterIcon}
              onClick={() => setShowFilters(true)}
            />
          </Show>
          <Show if={variantView.showScreeningTabs}>
            <div className="md:w-fit">
              <Tabs<UnitTabs>
                tabStyle="outline"
                size="sm"
                onChange={(t) => setCurrentTab(t)}
                active={currentTab}
                tabs={UNIT_TABS}
                className="h-10"
              />
            </div>
          </Show>

          <Show if={variantView.showScreeningTabs && isUnitsDashboard}>
            <div className="md:w-fit">
              <Tabs<ListedFilter>
                tabStyle="outline"
                size="sm"
                onChange={(next) => {
                  setListedTab(next);
                  setSearchParams({ ...searchParams, page: "1" });
                }}
                active={listedTab}
                tabs={LISTED_TABS}
                className="h-10"
              />
            </div>
          </Show>
          <Show if={type.isListingFraudAlerts && isListings}>
            <div className="md:w-fit">
              <Tabs<FraudAlertsTab>
                tabStyle="outline"
                size="sm"
                onChange={(next) => {
                  setFraudAlertsTab(next);
                  setSearchParams({ ...searchParams, page: "1" });
                }}
                active={fraudAlertsTab}
                tabs={FRAUD_ALERTS_TABS}
                className="h-10"
              />
            </div>
          </Show>
          {isCardView ? (
            <VisibilityPanel
              heading="Visibility"
              triggerLabel="Card fields"
              items={cardFields.columns}
              triggerIcon={FilterVerticalIcon}
              onToggle={(id, checked) =>
                cardFields.commitNow(
                  cardFields.columns.map((f) =>
                    f.id === id ? { ...f, checked } : f
                  )
                )
              }
            />
          ) : (
            <VisibilityPanel
              heading="Column visibility"
              triggerLabel="Columns"
              items={tableColumns.columns}
              triggerIcon={FilterVerticalIcon}
              onToggle={(id, checked) =>
                tableColumns.commitNow(
                  tableColumns.columns.map((c) =>
                    c.id === id ? { ...c, checked } : c
                  )
                )
              }
            />
          )}
        </div>
      </div>
      {isCardView ? (
        <UnitsCardGrid
          units={listedCardUnits}
          isLoading={
            cardsQuery.isLoading ||
            (listedCardUnits.length === 0 && cardsQuery.isFetching)
          }
          emptyTitle={
            fraudAlertsEmptyState?.title ?? LISTINGS_EMPTY_STATE.title
          }
          emptyIcon={fraudAlertsEmptyState?.Icon}
          emptyText={fraudAlertsEmptyState?.text ?? variantView.cardEmptyText}
          onActivityClick={handleActivityClick}
          visibleFields={visibleCardFields}
          hasNextPage={Boolean(cardsHasNextPage)}
          isFetchingNextPage={cardsFetchingNext}
          onLoadMore={loadMoreCards}
          fromListings={isListings}
        />
      ) : (
        <UnitsTable
          hiddenColumnIds={hiddenTableColumnIds}
          currentProperty={property}
          handleAvailableForApplication={handleAvailableForApplication}
          data={tableRows}
          handleCopyLink={handleCopyLink}
          hasSearchParams={variantView.hasSearchParams}
          emptyState={fraudAlertsEmptyState ?? variantView.emptyState}
          isLoading={isFetching && !isRefetching}
          setSelectedUnitId={setSelectedUnitId}
          setShowInviteApplicantsModal={setShowInviteApplicantsModal}
          setShowDeleteUnitModal={setShowDeleteUnitModal}
          hideProperty={!isUnitsDashboard}
          sorting={sorting}
          setSorting={setSorting}
          viewPortHeightOffset={viewPortHeightOffset}
          onAddUnit={canAddUnit ? () => setShowAddUnitModal(true) : undefined}
          fromListings={isListings}
          pagination={
            <div className="flex justify-end">
              <Pagination
                className="md:justify-end justify-center"
                total={data?.page_count || 0}
                current={Number(searchParams.page)}
                onChange={(p) =>
                  setSearchParams({ ...searchParams, page: String(p) })
                }
              />
            </div>
          }
        />
      )}
      <UnitsFiltersDrawer
        open={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        liveOnListings={isListings}
        onApply={(next) => {
          setFilters(next);
          setSearchParams({ ...searchParams, page: "1" });
        }}
      />
      <DeleteUnitModal
        opened={showDeleteUnitModal}
        onClose={() => setShowDeleteUnitModal(false)}
        onDelete={deleteUnit}
      />
      <AddUnitModal
        propertiesMeta={queryProperties}
        opened={showAddUnitModal}
        onClose={() => setShowAddUnitModal(false)}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
      />

      <Show
        if={
          Boolean(selectedUnitId) && showInviteApplicantsModal && propertyIdProp
        }
      >
        {/* <InviteApplicantsModal
          opened={showInviteApplicantsModal}
          onClose={() => setShowInviteApplicantsModal(false)}
          propertyId={propertyIdProp}
          propertyName={propertyName}
          unitId={selectedUnitId}
          unitName={unitName}
          queryKey={[
            query,
            {
              params: {
                page: searchParams.page,
                search: debouncedParams,
                per_page: 10
              }
            }
          ]}
        /> */}
        <InviteAndCreateModal
          open={showInviteApplicantsModal}
          setOpen={setShowInviteApplicantsModal}
          unitId={selectedUnitId}
          propertyId={propertyIdProp}
          propertyName={propertyName}
          unitName={unitName}
          onInviteRefreshKey={[
            query,
            {
              params: {
                page: searchParams.page,
                search: debouncedParams,
                per_page: 10
              }
            }
          ]}
          unitSelectSkipped={false}
        />
      </Show>
    </div>
  );
};
